/**
 * Helper for sending chat messages via HTTP.
 */

import {
  confirmOptimisticMessage,
  movePendingToConversation,
  removeOptimisticMessage,
} from '@saaga/shared/store/slices/chat.slice'
import type { ChatMessageEntity } from '@saaga/shared/store/slices/chat-message-entity.interface'
import type { AppDispatch } from '@saaga/shared/store/store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { logger } from '@saaga/shared/utils/logger.util'

import type { SendChatMessageMutation } from './chat-message-send.interface'

interface SendViaHttpParams {
  content: string
  isNote: boolean
  optimisticEntity: ChatMessageEntity
  conversationId: string | null
  agencySlug: string | null
  dispatch: AppDispatch
  sendChatMessage: SendChatMessageMutation
  onNewConversation: (newConversationId: string) => void
  attachmentIdList?: string[]
}

export const sendViaHttp = async ({
  content,
  isNote,
  optimisticEntity,
  conversationId,
  agencySlug,
  dispatch,
  sendChatMessage,
  onNewConversation,
  attachmentIdList,
}: SendViaHttpParams): Promise<void> => {
  const effectiveConversationId = conversationId ?? 'pending'

  try {
    const response = await sendChatMessage({
      content,
      conversation_id: conversationId ?? undefined,
      agency_slug: agencySlug ?? undefined,
      is_note: isNote || undefined,
      attachment_ids: attachmentIdList?.length ? attachmentIdList : undefined,
    }).unwrap()

    dispatch(
      confirmOptimisticMessage({
        conversationId: effectiveConversationId,
        optimisticId: optimisticEntity.id,
        realId: response.message.id,
        attachmentList: response.message.attachments,
      })
    )

    if (response.is_new_conversation) {
      dispatch(movePendingToConversation({ targetConversationId: response.conversation_id }))
      trackEvent('conversation_created', { agencySlug: agencySlug ?? null })
      onNewConversation(response.conversation_id)
    }
  } catch (error) {
    logger.error('Failed to send message via HTTP', error)
    dispatch(
      removeOptimisticMessage({
        conversationId: effectiveConversationId,
        messageId: optimisticEntity.id,
      })
    )
  }
}
